/* Shared read-side model of a buyer's profile: the labels, Buy Box tags and
   Profile Strength scores. The buyer's own My Profile card and the staff
   buyer-profile modal both render from here so the two views can't drift
   apart (they had: one showed Pre-Qualified, the other didn't). */

export const EXPERIENCE_LABELS: Record<string, string> = {
  first_time: "First-time buyer",
  bought_1_2: "Bought 1–2 businesses",
  bought_3_plus: "Bought 3+ businesses",
};
// Same wording as the editor's YEARS_OPTIONS labels.
export const YEARS_LABELS: Record<string, string> = {
  any: "Any",
  under_1: "< 1 Year",
  "1_3": "1 – 3 Years",
  "3_5": "3 – 5 Years",
  "5_10": "5 – 10 Years",
  "10_plus": "10+ Years",
};
export const PREQUAL_LABELS: Record<string, string> = {
  sba: "SBA Pre-Qualified",
  private_lender: "Private Lender Pre-Qualified",
};
export const FUNDING_LABELS: Record<string, string> = {
  sba_loan: "SBA Loan",
  seller_financing: "Seller Financing",
  self_funded: "Self-Funded",
  private_lenders: "Private Lenders",
};
// sde_preference is a MINIMUM annual SDE (see app/services/business_listing.py).
export const SDE_LABELS: Record<string, string> = {
  "100k": "$100K",
  "250k": "$250K",
  "500k": "$500K",
  "1m_plus": "$1M+",
};
export const SDE_THRESHOLDS: Record<string, number> = {
  "100k": 100_000,
  "250k": 250_000,
  "500k": 500_000,
  "1m_plus": 1_000_000,
};

export function fmtMoney(n: number): string {
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `$${m % 1 === 0 ? m : m.toFixed(1)}M`;
  }
  return `$${Math.round(n / 1000)}K`;
}

/** A minimum SDE above the whole budget can't match anything — the backend
 *  now rejects it, but profiles saved before that rule still carry it. */
export function sdeExceedsBudget(sde: string | null | undefined, budgetMax: number | null | undefined): boolean {
  const floor = sde ? SDE_THRESHOLDS[sde] : undefined;
  return floor != null && budgetMax != null && floor > budgetMax;
}

/** The subset of buyer preferences + identity both views read. */
export interface BuyerProfileInput {
  phone: string | null;
  avatar_url: string | null;
  experience: string | null;
  budget_min: number | null;
  budget_max: number | null;
  category_ids: number[];
  other_category: string | null;
  years_in_business: string | null;
  funding_methods: string[];
  prequalifications?: string[];
  additional_notes: string | null;
  preferred_location: string | null;
  preferred_country_names?: string[] | null;
  preferred_state_names?: string[] | null;
  preferred_city_labels?: string[] | null;
  preferred_city_names?: string[] | null;
  sde_preference: string | null;
}

export interface StrengthItem {
  label: string;
  pct: number;
  /** What to add to raise this bar; null once it's full. */
  hint: string | null;
}

export function describeBuyerProfile(p: BuyerProfileInput, categories: { id: number; name: string }[]) {
  const industries = (p.category_ids ?? [])
    .map((id) => categories.find((c) => c.id === id)?.name)
    .filter((n): n is string => Boolean(n));
  if (p.other_category) industries.push(p.other_category);

  // Structured names when the profile has them; legacy profiles only carry the
  // flattened ", "-joined string.
  const cities = [...(p.preferred_city_labels ?? []), ...(p.preferred_city_names ?? [])];
  const states = p.preferred_state_names ?? [];
  const countries = p.preferred_country_names ?? [];
  const structured = cities.length + states.length + countries.length > 0;
  const geographies = structured
    ? [...cities, ...states, ...(cities.length || states.length ? [] : countries)]
    : (p.preferred_location ?? "").split(", ").filter(Boolean);
  const location = structured
    ? [
        cities.length ? `Cities: ${cities.join(", ")}` : null,
        states.length ? `States: ${states.join(", ")}` : null,
        !cities.length && !states.length && countries.length ? `Countries: ${countries.join(", ")}` : null,
      ].filter(Boolean).join(" · ")
    : p.preferred_location || null;

  const budgetMin = p.budget_min ?? null;
  const budgetMax = p.budget_max ?? null;
  const hasBudget = budgetMin != null || budgetMax != null;
  const budgetLabel = !hasBudget
    ? null
    : budgetMax == null
    ? `${fmtMoney(budgetMin ?? 0)}+`
    : budgetMin == null || budgetMin === 0
    ? `Up to ${fmtMoney(budgetMax)}`
    : `${fmtMoney(budgetMin)}–${fmtMoney(budgetMax)}`;
  const sde = p.sde_preference ? SDE_LABELS[p.sde_preference] ?? p.sde_preference : null;
  const years = p.years_in_business ? YEARS_LABELS[p.years_in_business] ?? p.years_in_business : null;
  const experience = p.experience ? EXPERIENCE_LABELS[p.experience] ?? p.experience : null;
  const funding = (p.funding_methods ?? []).map((m) => FUNDING_LABELS[m] ?? m);
  const prequal = (p.prequalifications ?? []).map((m) => PREQUAL_LABELS[m] ?? m);

  const tags: string[] = [
    ...prequal,
    ...industries,
    ...geographies,
    ...(budgetLabel ? [budgetLabel] : []),
    ...(sde ? [`Min SDE ${sde}`] : []),
    ...(years ? [years] : []),
  ];

  /* Profile Strength — each bar measures what its label says:
       Buy Box clarity   = the five search criteria
       Financial         = how the buyer pays + whether a lender backs them
       Contact & identity = what lets a seller actually reach a real person
       Additional notes  = free-text context */
  const pick = (checks: [boolean, string][]) => {
    const done = checks.filter(([ok]) => ok).length;
    return {
      pct: Math.round((done / checks.length) * 100),
      hint: checks.find(([ok]) => !ok)?.[1] ?? null,
    };
  };
  const buyBox = pick([
    [industries.length > 0, "Add target industries"],
    [geographies.length > 0, "Add a target location"],
    [hasBudget, "Set a budget range"],
    [Boolean(sde), "Set a minimum SDE"],
    [Boolean(years), "Choose years in business"],
  ]);
  const financial = pick([
    [Boolean(experience), "Add your buying experience"],
    [funding.length > 0, "Add a financing method"],
    [prequal.length > 0, "Get SBA or lender pre-qualified"],
  ]);
  const contact = pick([
    [Boolean(p.phone), "Add a phone number"],
    [Boolean(p.avatar_url), "Upload a profile photo"],
  ]);
  const notes = pick([[Boolean(p.additional_notes), "Add notes for sellers"]]);
  const strength: StrengthItem[] = [
    { label: "Buy Box clarity", ...buyBox },
    { label: "Financial qualification", ...financial },
    { label: "Contact & identity", ...contact },
    { label: "Additional notes", ...notes },
  ];
  const overallPct = Math.round(strength.reduce((s, i) => s + i.pct, 0) / strength.length);
  const nextStep = strength.find((i) => i.hint)?.hint ?? null;

  return {
    industries,
    location,
    // Location split by level so views can show cities and states as separate
    // fields. `legacyLocations` is set only for profiles saved before the
    // structured picker, which carry nothing but the flattened string.
    cities,
    states,
    countries: cities.length || states.length ? [] : countries,
    legacyLocations: structured ? [] : geographies,
    budgetLabel,
    budgetMin,
    budgetMax,
    sde,
    years,
    experience,
    funding,
    prequal,
    tags,
    strength,
    overallPct,
    nextStep,
    sdeConflict: sdeExceedsBudget(p.sde_preference, budgetMax),
  };
}

/** "Active Buyer" only when the account really is active. */
export function buyerStatusLabel(status: string | null | undefined): string {
  if (!status || status === "active") return "Active Buyer";
  const s = status.replace(/_/g, " ");
  return `${s.charAt(0).toUpperCase()}${s.slice(1)} account`;
}
